import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = [
  { value: 'available', label: 'Available now', dot: 'bg-emerald-500' },
  { value: 'busy', label: 'Busy', dot: 'bg-amber-500' },
  { value: 'offline', label: 'Offline', dot: 'bg-cn-text-muted' },
];

export const AvailabilityToggle = ({ className = '' }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState('available');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/clinician/availability`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setStatus(data.status || 'available'))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const change = async (next) => {
    setStatus(next);
    setSaving(true);
    try {
      await axios.put(`${API}/clinician/availability`, { status: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`You're ${next === 'available' ? 'now available' : next}`);
    } catch (e) {
      toast.error('Could not update availability');
    } finally {
      setSaving(false);
    }
  };

  const current = STATES.find((s) => s.value === status) || STATES[0];

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="availability-toggle">
      <div className="text-xs uppercase tracking-wider text-cn-text-muted font-semibold hidden md:block">Status</div>
      <div className="inline-flex p-1 bg-white border border-cn-border rounded-full">
        {STATES.map((s) => (
          <button
            key={s.value}
            onClick={() => change(s.value)}
            disabled={loading || saving}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              status === s.value
                ? 'bg-cn-primary text-white'
                : 'text-cn-text-muted hover:text-cn-text'
            }`}
            data-testid={`availability-${s.value}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${status === s.value ? 'bg-white' : s.dot} ${s.value === 'available' ? 'animate-pulse' : ''}`} />
            {s.label}
          </button>
        ))}
        {(loading || saving) && <Loader2 className="w-3 h-3 animate-spin text-cn-primary ml-1 self-center" />}
      </div>
    </div>
  );
};

export default AvailabilityToggle;
